/**
 * t368u: the HR record on a phone. Your own (Settings › My account › HR record) you can edit, a section at a time, in a
 * sheet; someone else's (People › person › HR record) is read-only, with Approve / Reject for their pending changes.
 * Rows come from hrViewSections (shared), so the iOS app shows exactly the same thing. Show is audited and hides after 30s.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { Eye, EyeOff } from 'lucide-react';
import { hrChangeLine, hrPendingTitle, hrViewSections, type HrChangeRequest, type HrFieldSection, type HrViewRow } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { Input, Spinner } from '@/components/ui';
import { refreshHr, useHrRecord } from '@/features/hr/hooks';
import { SectionForm, useReveal } from '@/features/hr/HrRecord';
import { BottomSheet } from '../BottomSheet';
import { PhoneHrDocuments } from './PersonHrDocs';
import { card, ghostBtn, head, saveBtn } from './PersonSettings';

function Row({ r, rv }: { r: HrViewRow; rv: ReturnType<typeof useReveal> }) {
  const s = rv.shown[r.key];
  return (
    <div className="flex items-center gap-2 border-b border-slate-100 py-2 last:border-0 dark:border-slate-800" data-testid={`m-hr-row-${r.key}`}>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-slate-500">{r.label}</div>
        <div className={clsx('break-words text-[15px]', r.muted && 'text-slate-500', s && 'font-mono')} data-testid={`m-hr-value-${r.key}`}>{s ? s.value : r.value}</div>
        {s && <div className="text-xs text-slate-500">Hidden again in {rv.left(r.key)}s</div>}
        {!s && r.note && <div className="text-xs text-slate-500" data-testid={`m-hr-note-${r.key}`}>{r.note}</div>}
      </div>
      {r.reveal && <button type="button" className="flex h-11 min-w-11 items-center justify-center gap-1 rounded-full px-2 text-sm text-slate-600 active:bg-slate-100 dark:text-slate-300 dark:active:bg-slate-800"
        onClick={() => (s ? rv.hide(r.key) : void rv.reveal(r.key, r.reveal!))} data-testid={`m-hr-${s ? 'hide' : 'show'}-${r.key}`}>
        {s ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}{s ? 'Hide' : 'Show'}</button>}
    </div>
  );
}

/** Without hr.edit (`canApprove` false) changes are listed with no controls. */
function Pending({ items, self, canApprove }: { items: HrChangeRequest[]; self: boolean; canApprove: boolean }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState<HrChangeRequest | null>(null);
  const [note, setNote] = useState('');
  const act = async (f: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await f(); toast(ok, 'success'); setRejecting(null); setNote(''); void refreshHr(ws); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <>
      <div className={head}>{hrPendingTitle(self, canApprove)}</div>
      <div className={card} data-testid="m-hr-pending">
        {items.map(c => (
          <div key={c.id} className="border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
            <div className="text-[15px]">{hrChangeLine(c)}</div>
            {self && <button type="button" className={clsx(ghostBtn, 'mt-2 h-11 w-full')} disabled={busy} onClick={() => void act(() => api.hr.cancel(ws!, c.id), 'Request cancelled')}>Cancel request</button>}
            {!self && canApprove && (c.can_decide
              ? <div className="mt-2 flex gap-2">
                <button type="button" className={clsx(saveBtn, 'h-11')} disabled={busy} onClick={() => void act(() => api.hr.approve(ws!, c.id), 'Approved')} data-testid={`m-hr-approve-${c.id}`}>Approve</button>
                <button type="button" className={clsx(ghostBtn, 'h-11 flex-1')} disabled={busy} onClick={() => setRejecting(c)}>Reject</button>
              </div>
              : <div className="mt-1 text-xs text-slate-500">Needs someone who can see bank details</div>)}
          </div>
        ))}
      </div>
      {!self && canApprove && <BottomSheet open={Boolean(rejecting)} onClose={() => setRejecting(null)} title="Reject this change?" testId="m-hr-reject"
        footer={<button type="button" className={clsx(saveBtn, 'w-full')} disabled={busy} onClick={() => rejecting && void act(() => api.hr.reject(ws!, rejecting.id, note || null), 'Rejected')}>Reject</button>}>
        <div className="px-4 pb-3">
          <p className="mb-2 text-sm">{rejecting ? hrChangeLine(rejecting) : ''}</p>
          <Input width="full" placeholder="Why (optional, they'll see this)" maxLength={300} value={note} onChange={e => setNote(e.target.value)} aria-label="Reason" />
        </div>
      </BottomSheet>}
    </>
  );
}

export function PhoneHrRecord({ who, readOnly }: { who: string; readOnly?: boolean }) {
  const q = useHrRecord(who);
  const rv = useReveal(who);
  const [editing, setEditing] = useState<HrFieldSection | null>(null);
  if (q.isLoading) return <div className="p-6"><Spinner /></div>;
  if (!q.data) return <div className="flex-1 overflow-y-auto pb-6"><p className="p-4 text-sm text-slate-600 dark:text-slate-300" data-testid="m-hr-unavailable">{errorMessage(q.error, 'These personal details aren’t available')}</p><PhoneHrDocuments who={who} /></div>;
  const rec = q.data;
  const sections = hrViewSections(rec);
  const canEdit = (id: HrFieldSection) => !readOnly && rec.self && (id === 'bank' ? rec.can.edit_bank : rec.can.edit)
    && (id !== 'additional' || rec.values.some(v => v.can_edit && rec.fields.find(f => f.id === v.field_id)?.section === 'additional'));
  const editingTitle = sections.find(s => s.id === editing)?.title ?? '';
  return (
    <div className="flex-1 overflow-y-auto pb-6" data-testid="m-hr-record">
      {!rec.complete && <p className="px-4 pt-3 text-sm text-slate-500" data-testid="m-hr-missing">Still to fill in: {rec.missing.join(', ')}</p>}
      {rec.pending.length > 0 && <Pending items={rec.pending} self={rec.self} canApprove={rec.can.approve} />}
      {sections.map(s => (
        <section key={s.id} data-testid={`m-hr-section-${s.id}`}>
          <div className={clsx(head, 'flex items-center')}>
            <span className="flex-1">{s.title}</span>
            {canEdit(s.id) && <button type="button" className="-my-2 h-9 px-2 text-sm font-medium normal-case tracking-normal text-slate-700 dark:text-slate-200" onClick={() => setEditing(s.id)} data-testid={`m-hr-edit-${s.id}`}>{s.id === 'bank' && rec.bank?.on_file ? 'Change' : 'Edit'}</button>}
          </div>
          <div className={card}>{s.rows.map(r => <Row key={r.key} r={r} rv={rv} />)}</div>
          {s.note && <p className="px-4 pt-1 text-xs text-slate-500">{s.note}</p>}
        </section>
      ))}
      {!sections.length && <p className="p-4 text-sm text-slate-500">No HR sections are turned on.</p>}
      <PhoneHrDocuments who={who} />
      <BottomSheet open={Boolean(editing)} onClose={() => setEditing(null)} title={editingTitle} testId="m-hr-edit">
        {editing && <div className="px-4 pb-4"><SectionForm rec={rec} section={editing} manager={false} onDone={() => setEditing(null)} /></div>}
      </BottomSheet>
    </div>
  );
}
